import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import "./Modal.css";

/** Use with `form={EDIT_PRODUCT_FORM_ID}` on a submit control outside the form tree. */
export const EDIT_PRODUCT_FORM_ID = "edit-harvest-product-form";

enum Actions {
  Add = "add",
  Subtract = "subtract",
}

export type EditProductFormSnapshot = {
  newQuantity: number;
  newPrice: number;
  newDiscount: number | null;
  hasUnsavedChanges: boolean;
};

export type EditProductFormProps = {
  productId: string;
  quantity: number;
  price: number;
  discount: number | null;
  isAdmin: boolean;
  onValidSubmit: (
    quantity: number,
    price: number,
    discount: number | null,
  ) => void;
  onValuesChange?: (snapshot: EditProductFormSnapshot) => void;
};

export function EditProductForm({
  productId,
  quantity,
  price,
  discount,
  isAdmin,
  onValidSubmit,
  onValuesChange,
}: EditProductFormProps) {
  const [newQuantity, setNewQuantity] = useState<number>(Math.max(quantity, 0));
  const [newPrice, setNewPrice] = useState<number>(0);
  const [newDiscount, setNewDiscount] = useState<number | null>(null);
  const [currentMathAction, setCurrentMathAction] = useState(Actions.Add);
  const [currentMathActionQuantity, setCurrentMathActionQuantity] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  const quantityInputRef = useRef<HTMLInputElement | null>(null);
  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;

  const discountValidationMessage =
    "Discount has to be a number between 0 and 100.";

  const defaultInputValue =
    currentMathActionQuantity > 0 ? currentMathActionQuantity : "";

  const hasUnsavedChanges = useMemo(() => {
    const normDiscount = (d: number | null) =>
      d === null || Number.isNaN(d) ? null : d;
    const discountDirty = normDiscount(newDiscount) !== normDiscount(discount);
    const priceDirty = isAdmin && newPrice !== price;
    const quantityDirty = newQuantity !== quantity;
    return quantityDirty || priceDirty || discountDirty;
  }, [newQuantity, quantity, isAdmin, newPrice, price, newDiscount, discount]);

  useEffect(() => {
    onValuesChangeRef.current?.({
      newQuantity,
      newPrice,
      newDiscount,
      hasUnsavedChanges,
    });
  }, [newQuantity, newPrice, newDiscount, hasUnsavedChanges]);

  useEffect(() => {
    setNewQuantity(Math.max(0, quantity));
    setNewPrice(price);
    setNewDiscount(discount);
    setCurrentMathActionQuantity(0);
    setCurrentMathAction(Actions.Add);
    setFormError(null);
  }, [quantity, price, discount, productId]);

  function updateNewQuantity(action: string, mathActionQuantity: number) {
    const quantityValue = mathActionQuantity ?? 0;
    const value =
      action && action === Actions.Add
        ? quantity + quantityValue
        : action && action === Actions.Subtract
          ? quantity - quantityValue
          : quantity;

    setNewQuantity(Math.max(value, 0));
  }

  function handleEditQuantity(newMathActionQuantity: number) {
    setFormError(null);
    setCurrentMathActionQuantity(newMathActionQuantity);
    updateNewQuantity(currentMathAction, newMathActionQuantity);
  }

  function actionChange(action: Actions) {
    setFormError(null);
    setCurrentMathAction(action);
    updateNewQuantity(action, currentMathActionQuantity);
  }

  function discountIsOutOfRange(d: number | null): boolean {
    if (d === null) return false;
    return !Number.isFinite(d) || d < 0 || d > 100;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (discountIsOutOfRange(newDiscount)) {
      setFormError(discountValidationMessage);
      return;
    }
    setFormError(null);
    onValidSubmit(Math.max(0, newQuantity), newPrice, newDiscount);
  }

  return (
    <form
      id={EDIT_PRODUCT_FORM_ID}
      className="edit-inputs"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="radio-input">
        <label>
          <input
            type="radio"
            name="volume"
            value="add"
            checked={currentMathAction === Actions.Add}
            onChange={() => actionChange(Actions.Add)}
          />
          Add to volume
        </label>
      </div>
      <div className="radio-input">
        <label>
          <input
            type="radio"
            name="volume"
            value="subtract"
            checked={currentMathAction === Actions.Subtract}
            onChange={() => actionChange(Actions.Subtract)}
          />
          Subtract from volume
        </label>
      </div>
      <div className="harvest-input">
        <label htmlFor="harvest-product-quantity">Quantity in kilos</label>

        <input
          className="input"
          type="number"
          name="harvestProductQuantity"
          id="harvest-product-quantity"
          min={0}
          value={defaultInputValue}
          placeholder="0"
          ref={quantityInputRef}
          onChange={(e) =>
            handleEditQuantity(parseFloat(e.currentTarget.value) || 0)
          }
        />
      </div>
      {isAdmin && (
        <div className="harvest-input">
          <label htmlFor="harvest-product-price">Price per kilo</label>
          <input
            className="input"
            type="number"
            name="harvestProductPrice"
            id="harvest-product-price"
            min={0}
            defaultValue={price}
            onChange={(e) => {
              setFormError(null);
              setNewPrice(parseFloat(e.currentTarget.value));
            }}
          />
        </div>
      )}
      <div className="harvest-input">
        <label htmlFor="harvest-product-discount">Discount %</label>

        <input
          className="input"
          type="number"
          name="harvestProductDiscount"
          id="harvest-product-discount"
          min={0}
          max={100}
          defaultValue={discount ?? 0}
          onChange={(e) => {
            setFormError(null);
            const v = parseFloat(e.currentTarget.value);
            setNewDiscount(Number.isNaN(v) ? null : v);
          }}
        />
      </div>
      <p>Your new produce volume will be {newQuantity}</p>
      {formError && (
        <p className="pickup-form-validation-msg" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
}
